import { supabase } from './supabase';

class NotificationService {
  // Get all notifications for the current user
  async getUserNotifications(limit = 50, offset = 0) {
    try {
      const { data, error } = await supabase
        .from('notifications')
        .select('*')
        .order('created_at', { ascending: false })
        .range(offset, offset + limit - 1);

      if (error) {
        console.error('Error fetching notifications:', error);
        return { success: false, error: error.message };
      }

      return { success: true, data: data || [] };
    } catch (error) {
      console.error('Error in getUserNotifications:', error);
      return { success: false, error: error.message };
    }
  }

  // Get unread notification count
  async getUnreadCount() {
    try {
      const { data, error } = await supabase
        .rpc('get_unread_notification_count');

      if (error) {
        console.error('Error fetching unread count:', error);
        return { success: false, error: error.message, count: 0 };
      }

      return { success: true, count: data || 0 };
    } catch (error) {
      console.error('Error in getUnreadCount:', error);
      return { success: false, error: error.message, count: 0 };
    }
  }

  // Mark a specific notification as read
  async markAsRead(notificationId) {
    try {
      const { data, error } = await supabase
        .rpc('mark_notification_as_read', { notification_id: notificationId });

      if (error) {
        console.error('Error marking notification as read:', error);
        return { success: false, error: error.message };
      }

      return { success: true, data };
    } catch (error) {
      console.error('Error in markAsRead:', error);
      return { success: false, error: error.message };
    }
  }

  // Mark all notifications as read
  async markAllAsRead() {
    try {
      const { data, error } = await supabase
        .rpc('mark_all_notifications_as_read');

      if (error) {
        console.error('Error marking all notifications as read:', error);
        return { success: false, error: error.message };
      }

      return { success: true, count: data || 0 };
    } catch (error) {
      console.error('Error in markAllAsRead:', error);
      return { success: false, error: error.message };
    }
  }

  // Create a new notification (admin only)
  async createNotification(userId, type, title, message, actionUrl = null, metadata = {}) {
    try {
      const { data, error } = await supabase
        .rpc('create_notification', {
          target_user_id: userId,
          notification_type: type,
          notification_title: title,
          notification_message: message,
          notification_action_url: actionUrl,
          notification_metadata: metadata
        });

      if (error) {
        console.error('Error creating notification:', error);
        return { success: false, error: error.message };
      }

      return { success: true, data };
    } catch (error) {
      console.error('Error in createNotification:', error);
      return { success: false, error: error.message };
    }
  }

  // Create system announcement (admin only)
  async createSystemAnnouncement(title, message, actionUrl = null) {
    try {
      const { data, error } = await supabase
        .rpc('create_system_announcement', {
          announcement_title: title,
          announcement_message: message,
          announcement_action_url: actionUrl
        });

      if (error) {
        console.error('Error creating system announcement:', error);
        return { success: false, error: error.message };
      }

      return { success: true, count: data || 0 };
    } catch (error) {
      console.error('Error in createSystemAnnouncement:', error);
      return { success: false, error: error.message };
    }
  }

  // Subscribe to real-time notification updates
  subscribeToNotifications(userId, callback) {
    const subscription = supabase
      .channel('notifications')
      .on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'notifications',
          filter: `user_id=eq.${userId}`
        },
        (payload) => {
          console.log('Notification update:', payload);
          callback(payload);
        }
      )
      .subscribe();

    return subscription;
  }

  // Unsubscribe from real-time updates
  unsubscribeFromNotifications(subscription) {
    if (subscription) {
      supabase.removeChannel(subscription);
    }
  }

  // Get notification icon based on type
  getNotificationIcon(type) {
    const icons = {
      'game_update': '🎮',
      'subscription_update': '💳',
      'system_announcement': '📢',
      'game_rating': '⭐',
      'studio_update': '🏢',
      'payment_update': '💰',
      'friend_request': '👥',
      'friend_accepted': '🤝'
    };
    return icons[type] || '🔔';
  }

  // Get notification color based on type
  getNotificationColor(type) {
    const colors = {
      'game_update': 'text-blue-500',
      'subscription_update': 'text-green-500',
      'system_announcement': 'text-purple-500',
      'game_rating': 'text-yellow-500',
      'studio_update': 'text-indigo-500',
      'payment_update': 'text-emerald-500',
      'friend_request': 'text-blue-500',
      'friend_accepted': 'text-green-500'
    };
    return colors[type] || 'text-gray-500';
  }

  // Format notification time
  formatNotificationTime(timestamp) {
    const now = new Date();
    const notificationTime = new Date(timestamp);
    const diffInMinutes = Math.floor((now - notificationTime) / (1000 * 60));

    if (diffInMinutes < 1) {
      return 'Just now';
    } else if (diffInMinutes < 60) {
      return `${diffInMinutes}m ago`;
    } else if (diffInMinutes < 1440) {
      const hours = Math.floor(diffInMinutes / 60);
      return `${hours}h ago`;
    } else {
      const days = Math.floor(diffInMinutes / 1440);
      return `${days}d ago`;
    }
  }
}

const notificationService = new NotificationService();
export default notificationService;
